import { format, isSameDay, parseISO, subDays } from 'date-fns';
import { MonthFull } from './date-helper';

type ConversationHeader = Pick<ConversationListItem, 'kind' | 'state' | 'students' | 'staffGroupName' | 'ownerGuardian'>;

const joinPt = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

export const conversationTitle = (conversation: ConversationHeader): string => {
  switch (conversation.kind) {
    case 'GuardianConversation': {
      const guardian = conversation.ownerGuardian?.name;
      if (conversation.state === 'GuardianHistory') return `Histórico de ${guardian || 'responsável removido'}`;

      return guardian || 'Responsável removido';
    }
    case 'StaffGroup':
      return conversation.staffGroupName || 'Grupo da equipe';
    default:
      return conversation.students[0]?.name || 'Aluno removido';
  }
};

export const conversationSubtitle = (conversation: ConversationHeader): string => {
  switch (conversation.kind) {
    case 'GuardianConversation': {
      const names = conversation.students.map((student) => student.name).filter(Boolean);

      return names.length > 0 ? `Responsável por ${joinPt(names)}` : 'Responsável sem alunos na escola';
    }
    case 'StaffGroup':
      return 'Grupo da equipe';
    default:
      return conversation.students[0]?.classroomName || '';
  }
};

export const totalUnread = (conversations: Pick<ConversationListItem, 'unreadCount'>[]): number =>
  conversations.reduce((sum, conversation) => sum + (conversation.unreadCount || 0), 0);

// The wall clock is already in the school's zone; "today" is the browser's, which is the school's for its staff.
export const dayLabel = (at: EventDateTime, now: Date = new Date()): string => {
  const date = parseISO(at.local);

  if (isSameDay(date, now)) return 'hoje';
  if (isSameDay(date, subDays(now, 1))) return 'ontem';

  const day = `${date.getDate()} de ${MonthFull[date.getMonth()]}`;

  return date.getFullYear() === now.getFullYear() ? day : `${day} de ${date.getFullYear()}`;
};

export const messageTime = (at: EventDateTime): string => format(parseISO(at.local), 'HH:mm');

export const messageTimestamp = (at: EventDateTime, now: Date = new Date()): string =>
  `${dayLabel(at, now)}, ${messageTime(at)}`;

export type MessageRow =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'block'; key: string; author: ConversationPerson; messages: ConversationMessage[] };

// As in a WhatsApp group: a day separator, then one author's consecutive messages under a single name.
export const messageRows = (messages: ConversationMessage[], now: Date = new Date()): MessageRow[] => {
  const rows: MessageRow[] = [];
  let day: string = null;

  for (const message of messages) {
    const label = dayLabel(message.sentAt, now);
    if (label !== day) {
      rows.push({ kind: 'day', key: `day-${message.id}`, label });
      day = label;
    }

    const last = rows[rows.length - 1];
    if (last.kind === 'block' && last.author.uuid === message.author.uuid) last.messages.push(message);
    else rows.push({ kind: 'block', key: message.id, author: message.author, messages: [message] });
  }

  return rows;
};

export type ReadTicks = 'sent' | 'readBySome' | 'readByAll';

export const READ_TICKS_LABEL: Record<ReadTicks, string> = {
  sent: 'Enviada',
  readBySome: 'Lida por parte dos responsáveis',
  readByAll: 'Lida pelos responsáveis',
};

// Only the school's messages carry ticks: what staff want to know is whether the family read them. A guardian who
// read and has since left the conversation still counts as a read.
export const readTicks = (message: ConversationMessage, participants: ConversationParticipant[]): ReadTicks | null => {
  if (message.author.kind !== 'Staff') return null;

  const guardianReaders = new Set(message.readBy.filter((read) => read.reader.kind === 'Guardian').map((read) => read.reader.uuid));
  if (guardianReaders.size === 0) return 'sent';

  const guardians = participants.filter((participant) => participant.person.kind === 'Guardian');

  return guardians.every((guardian) => guardianReaders.has(guardian.person.uuid)) ? 'readByAll' : 'readBySome';
};

export const notReadYet = (message: ConversationMessage, participants: ConversationParticipant[]): ConversationPerson[] =>
  participants
    .map((participant) => participant.person)
    .filter((person) => person.uuid !== message.author.uuid && !message.readBy.some((read) => read.reader.uuid === person.uuid));

const AUTHOR_COLORS = ['#1f7a6d', '#b5426b', '#6a4fb3', '#c0571f', '#2e6fb5', '#5f7a16'];

export const authorColor = (uuid: string): string => {
  let hash = 0;
  for (const char of uuid) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;

  return AUTHOR_COLORS[hash % AUTHOR_COLORS.length];
};

const sentAtMs = (message: ConversationMessage) => parseISO(message.sentAt.instant).getTime();

// A poll comes in change order, so a message whose read list changed lands after newer ones: known ids are
// replaced where they are, and the new ones go after them in sent order. A change to a message older than the
// screen holds is history not loaded yet; the older page brings it, current, when asked for.
export const mergeChanges = (onScreen: ConversationMessage[], changes: ConversationMessage[]): ConversationMessage[] => {
  const changed = new Map(changes.map((message) => [message.id, message]));
  const kept = onScreen.map((message) => changed.get(message.id) || message);
  const known = new Set(onScreen.map((message) => message.id));
  const oldestMs = onScreen.length > 0 ? sentAtMs(onScreen[0]) : -Infinity;
  const added = Array.from(changed.values())
    .filter((message) => !known.has(message.id) && sentAtMs(message) >= oldestMs)
    .sort((a, b) => sentAtMs(a) - sentAtMs(b));

  return [...kept, ...added];
};

export const mergeOlderPage = (onScreen: ConversationMessage[], older: ConversationMessage[]): ConversationMessage[] => {
  const known = new Set(onScreen.map((message) => message.id));

  return [...older.filter((message) => !known.has(message.id)), ...onScreen];
};

export const unconfirmedSends = (pending: PendingMessage[], onScreen: ConversationMessage[]): PendingMessage[] => {
  const stored = new Set(onScreen.map((message) => message.id));

  return pending.filter((message) => !stored.has(message.id));
};

// Only participants read: an administrator auditing it would get a 204 that records nothing.
export const messageToMarkRead = (
  isParticipant: boolean,
  onScreen: ConversationMessage[],
  lastMarked: string | null
): string | null => {
  const latest = onScreen[onScreen.length - 1];
  if (!isParticipant || !latest || latest.id === lastMarked) return null;

  return latest.id;
};

export const personName = (person: ConversationPerson): string => person.name || 'Pessoa removida';

const ROLE_LABEL: Record<ConversationPersonRole, string> = {
  Guardian: 'Responsável',
  Teacher: 'Professor(a)',
  Administrator: 'Administrador(a)',
};

const REASON_LABEL: Record<ConversationParticipantReason, string> = {
  GuardianOfTheStudent: 'responsável pelo aluno',
  GuardianWithAStudentAtTheSchool: 'responsável por aluno da escola',
  TeacherOfTheClassroom: 'da turma',
  AdministratorWhoWrote: 'entrou ao escrever',
  AdministratorOfTheSchool: 'administração da escola',
  AddedByAnAdministrator: 'adicionado(a) por um administrador',
};

export const roleLabel = (person: ConversationPerson): string => (person.role ? ROLE_LABEL[person.role] : '');

export const participantDescription = (participant: ConversationParticipant): string => {
  const reasons = participant.reasons.map((reason) => REASON_LABEL[reason]).join(', ');
  const role = roleLabel(participant.person);

  return role ? `${role} · ${reasons}` : reasons;
};

interface MessageContent {
  deleted: boolean;
  edited: boolean;
  text?: string;
  // url is missing when the API issued no download link.
  image?: { url?: string };
  file?: { name: string; url?: string };
}

export const messageContent = (message: ConversationMessage): MessageContent => {
  if (message.deletedAt) return { deleted: true, edited: false, text: 'Mensagem apagada' };

  const content: MessageContent = { deleted: false, edited: !!message.editedAt, text: message.text };
  if (message.file?.contentType.startsWith('image/')) content.image = { url: message.file.downloadUrl };
  else if (message.file) content.file = { name: message.file.originalName, url: message.file.downloadUrl };

  return content;
};

export const screenNotice = (
  screen: Pick<ConversationScreen, 'kind' | 'state' | 'isReadOnly' | 'isParticipant'>
): string | null => {
  if (screen.state === 'GuardianHistory') return 'Histórico da conversa com o responsável: somente leitura.';
  if (screen.state === 'Closed') return 'Grupo encerrado: somente leitura.';
  if (screen.state === 'ReadOnly') return 'O aluno foi removido: a conversa ficou somente leitura.';
  if (screen.isReadOnly) return 'Conversa somente leitura.';
  if (!screen.isParticipant) {
    return 'Você está vendo esta conversa como administrador(a), sem participar: abrir não marca nada como lido. Se escrever, você passa a participar.';
  }

  return null;
};

// The guardian reads it on an app version that can't show files, edits or deletes, so the API answers them 403.
export const composerHint = (screen: Pick<ConversationScreen, 'kind'>): string | null =>
  screen.kind === 'GuardianConversation'
    ? 'Somente texto: este responsável ainda usa a versão do app sem conversas por aluno.'
    : null;

interface GuardianNotSwitchedLink {
  uuid: string;
  name: string;
  // null when the caller can't open the guardian's conversation, or the guardian has none.
  linkTo: string | null;
}

export const guardiansNotSwitchedLinks = (screen: Pick<ConversationScreen, 'guardiansNotSwitched'>): GuardianNotSwitchedLink[] =>
  (screen.guardiansNotSwitched || []).map(({ guardian, guardianConversationId }) => ({
    uuid: guardian.uuid,
    name: personName(guardian),
    linkTo: guardianConversationId ? `/mensagens/${guardianConversationId}` : null,
  }));

// The module's limit; typing past it would only earn a 400.
export const MAX_MESSAGE_LENGTH = 4000;

// The module's routes answer errors without the legacy envelope; HttpService keeps their status.
export const sendErrorMessage = (error: any): string => {
  switch (error?.status) {
    case 403:
      return 'Esta conversa é somente leitura: a mensagem não foi enviada.';
    case 404:
      return 'Você não tem mais acesso a esta conversa.';
    case 400:
      return 'A mensagem não foi aceita: escreva um texto de até 4.000 caracteres.';
    default:
      return 'A mensagem não foi enviada. Tente de novo.';
  }
};

const lastMessagePreview = (message: ConversationMessage | undefined, currentPersonUuid: string): string => {
  if (!message) return 'Nenhuma mensagem ainda';

  const content = messageContent(message);
  const preview = content.text || (content.image ? '📷 Imagem' : content.file ? `📎 ${content.file.name}` : '');
  const author = message.author.uuid === currentPersonUuid ? 'Você' : personName(message.author);

  return `${author}: ${preview}`;
};

// The list has one short line for this, so it says less than the conversation's header does.
const listContext = (conversation: ConversationListItem): string => {
  switch (conversation.kind) {
    case 'GuardianConversation': {
      const names = conversation.students.map((student) => student.name).filter(Boolean);

      return names.length > 0 ? `resp. por ${joinPt(names)}` : 'sem alunos na escola';
    }
    case 'StaffGroup':
      return 'grupo da equipe';
    default:
      return conversation.students[0]?.classroomName || '';
  }
};

// As in WhatsApp's list: the time today, "ontem", then the date.
export const listTime = (at: EventDateTime, now: Date = new Date()): string => {
  const date = parseISO(at.local);

  if (isSameDay(date, now)) return messageTime(at);
  if (isSameDay(date, subDays(now, 1))) return 'ontem';

  return format(date, date.getFullYear() === now.getFullYear() ? 'dd/MM' : 'dd/MM/yyyy');
};

export interface ConversationListEntry {
  id: string;
  linkTo: string;
  title: string;
  context: string;
  // Empty for a guardian conversation nobody wrote in: its activity dates from the guardian's link or the cutover's
  // backfill, when nothing was said in it.
  time: string;
  preview: string;
  hasMessage: boolean;
  unreadCount: number;
  isReadOnly: boolean;
}

export const conversationListEntry = (
  conversation: ConversationListItem,
  currentPersonUuid: string,
  now: Date = new Date()
): ConversationListEntry => ({
  id: conversation.id,
  linkTo: `/mensagens/${conversation.id}`,
  title: conversationTitle(conversation),
  context: listContext(conversation),
  time:
    conversation.kind === 'GuardianConversation' && !conversation.lastMessage ? '' : listTime(conversation.lastActivityAt, now),
  preview: lastMessagePreview(conversation.lastMessage, currentPersonUuid),
  hasMessage: !!conversation.lastMessage,
  unreadCount: conversation.unreadCount || 0,
  isReadOnly: conversation.isReadOnly,
});
